import React, { useEffect, useState, useMemo, useContext, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../stores/store';
import { useLocation } from 'react-router-dom';
import ListLayout from '../../Generic/ListLayout';
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import useDeleteItem from '../../../hooks/useDeleteItem';
import useLocalizedTextsMap from '../../../hooks/useLocalizedTextsMap';
import { useSetMenu } from "../../../hooks/useSetMenu";
import {createNewGeneralColumn, createSpecialActionColumn } from '../../Generic/dataGridColumns';
import AuthContext from "../../../context/AuthProvider";
import useFetchPaginatedDataWithFilters from '../../../hooks/useFetchPaginatedDataWithFilters';
import DataTablePagination from '../../Generic/DataTablePagination';
import { useSnackbar } from "notistack";
import { useForm } from "react-hook-form";
import FormSelect from "../../Generic/FormSelect";
import { Tooltip } from "@mui/material";

const List = () => {
  const axiosPrivate = useAxiosPrivate();
  const { auth } = useContext(AuthContext);
  const { mainStore, schedulePlanStore } = useStore();
  const { t } = useTranslation();
  const localizedTextsMap = useLocalizedTextsMap();
  const { enqueueSnackbar } = useSnackbar();

  useSetMenu(mainStore.SchedulePlanMenuType, mainStore.SchedulePlanMenuNo);

  const location = useLocation();

  const { register, setValue, formState: { errors } } = useForm({
  });


  const [searchQuery, setSearchQuery] = useState(location.state?.search || "");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const deleteSchedulePlan = useDeleteItem(axiosPrivate, '/SchedulePlans', schedulePlanStore.deleteSchedulePlan);

  const [academicYears, setAcademicYears] = useState([]);
  const [selectedAcademicYearId, setSelectedAcademicYearId] = useState(null);

  const [semesters, setSemesters] = useState([]);
  const [selectedSemesterId, setSelectedSemesterId] = useState(null);

  const [selectedFacultyId, setSelectedFacultyId] = useState(null);
  const [faculties, setFaculties] = useState([]);

  const [levels, setLevels] = useState([]);
  const [selectedLevelId, setSelectedLevelId] = useState(null);

  const [semesterTypes, setSemesterTypes] = useState([]);
  const [selectedSemesterTypeId, setSelectedSemesterTypeId] = useState(null);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);

  const defaultAcademicYearSet = useRef(false);


  const loadSemesters = async (semesterTypeId) => {
    try {
      const response = await axiosPrivate.get(
        `/StaffPortalStaffs/GetSelectSemester/${auth.universityId}/${auth.userId}/${semesterTypeId || ''}`
      );
      setSemesters(response.data);
      setSelectedSemesterId(null);
    } catch (err) {
      enqueueSnackbar(t("loadDataError") + ":" + err, { variant: "error" });
    }
  };

  const loadFaculties = async (academicYearId = null) => {
    try {
      const url = academicYearId
        ? `/StaffPortalStaffs/GetSelectFaculty/${auth.universityId}/${auth.userId}?academicYearId=${academicYearId}`
        : `/StaffPortalStaffs/GetSelectFaculty/${auth.universityId}/${auth.userId}`;
      const response = await axiosPrivate.get(url);
      setFaculties(response.data);

      if (selectedFacultyId && !response.data.some(f => f.value === selectedFacultyId.value)) {
        setSelectedFacultyId(null);
        setValue('facultyId', null);
      }
    } catch (err) {
      enqueueSnackbar(t("loadDataError") + ":" + err, { variant: "error" });
    }
  };

  const loadLevels = async (academicYearId = null) => {
    try {
      const url = academicYearId
        ? `/StaffPortalStaffs/GetSelectLevel/${auth.universityId}/${auth.userId}?academicYearId=${academicYearId}`
        : `/StaffPortalStaffs/GetSelectLevel/${auth.universityId}/${auth.userId}`;
      const response = await axiosPrivate.get(url);
      setLevels(response.data);

      if (selectedLevelId && !response.data.some(l => l.value === selectedLevelId.value)) {
        setSelectedLevelId(null);
        setValue('levelId', null);
      }
    } catch (err) {
      enqueueSnackbar(t("loadDataError") + ":" + err, { variant: "error" });
    }
  };

  const loadSelectOptions = async () => {
    try {
      const [academicYearsResponse, facultiesResponse, levelResponse, semesterTypeResponse] = await Promise.all([
        axiosPrivate.get(`/StaffPortalStaffs/GetSelectAcademicYearInSemesterPlan/${auth.universityId}/${auth.userId}`),
        axiosPrivate.get(`/StaffPortalStaffs/GetSelectFaculty/${auth.universityId}/${auth.userId}`),
        axiosPrivate.get(`/StaffPortalStaffs/GetSelectLevel/${auth.universityId}/${auth.userId}`),
        axiosPrivate.get(`/StaffPortalStaffs/GetSelectSemesterType/${auth.universityId}/${auth.userId}`),

      ]);

      const academicYearsList = academicYearsResponse.data || [];
      setAcademicYears(academicYearsList);

      let defaultAcademicYearValue = null;
      if (academicYearsList.length > 0 && !defaultAcademicYearSet.current) {
        const defaultAcademicYear = academicYearsList[0];
        setSelectedAcademicYearId(defaultAcademicYear);
        setValue('academicYearId', defaultAcademicYear.value, { shouldValidate: true });
        defaultAcademicYearSet.current = true;
        defaultAcademicYearValue = defaultAcademicYear.value;
      }

      if (defaultAcademicYearValue) {
        await loadFaculties(defaultAcademicYearValue);
        await loadLevels(defaultAcademicYearValue);
      } else {
        setFaculties(facultiesResponse.data);
        setLevels(levelResponse.data);
      }

      const semesterTypesData = semesterTypeResponse.data;
      setSemesterTypes(semesterTypesData);
      const today = new Date();
      const currentYear = today.getFullYear();
      const startDate = new Date(`${currentYear}-02-05`);
      const endDate = new Date(`${currentYear}-08-15`);
      const selectedType = today >= startDate && today <= endDate ? 2 : 1;
      const selectedOption = semesterTypesData.find(item => item.value == selectedType);
      if (selectedOption) {
        setSelectedSemesterTypeId(selectedOption);
        await loadSemesters(selectedOption.value);
      } else {
        await loadSemesters();
      }
      
      setIsInitialLoadComplete(true);

    } catch (err) {
      enqueueSnackbar(t("loadDataError") + ":" + err, { variant: "error" });
      setIsInitialLoadComplete(true);
    }
  };

  const handleSelectChange = async (field, selectedOption) => {
    setValue(field, selectedOption?.value, { shouldValidate: true });
    switch (field) {
      case 'academicYearId':
        setSelectedAcademicYearId(selectedOption);
        if (selectedOption) {
          await Promise.all([
            loadFaculties(selectedOption.value),
            loadLevels(selectedOption.value)
          ]);
        } else {
          await Promise.all([
            loadFaculties(),
            loadLevels()
          ]);
        }
        break;
      case 'semesterId':
        setSelectedSemesterId(selectedOption);
        break;
      case 'facultyId':
        setSelectedFacultyId(selectedOption);
        break;
      case 'levelId':
        setSelectedLevelId(selectedOption);
        break;
      case 'semesterTypeId':
        setSelectedSemesterTypeId(selectedOption);
        loadSemesters(selectedOption?.value);
        break;
      default:
        break;
    }
    setPage(0);
  };
  useEffect(() => {
    loadSelectOptions();
  }, [t, auth]);


  const renderTwoLine = (primary, secondary) => (
    <div style={{ lineHeight: 1.25, padding: '6px 0', width: '100%' }}>
      <div style={{ fontWeight: 500 }}>{primary || '-'}</div>
      {secondary && (
        <div className="text-muted" style={{ fontSize: '0.72rem' }} title={secondary}>
          {secondary}
        </div>
      )}
    </div>
  );

  const columns = useMemo(() => {
    const colDefs = [
      createSpecialActionColumn(t, "/SchedulePlan/SchedulePlanWeekListByPlanId"),
      {
        field: "subjectName",
        headerName: t("schedulePlanSubject"),
        flex: 1.6,
        minWidth: 170,
        sortable: true,
        headerClassName: 'tableHeaderClassName',
        cellClassName: 'tableCellClassName',
        renderHeader: (params) => <strong>{params.colDef.headerName}</strong>,
        renderCell: (params) => renderTwoLine(params.row.subjectName, params.row.groupPlanNames),
      },
      {
        field: "timeSlotNames",
        headerName: t("schedulePlanTime"),
        flex: 1,
        minWidth: 110,
        sortable: true,
        headerClassName: 'tableHeaderClassName',
        cellClassName: 'tableCellClassName',
        renderHeader: (params) => <strong>{params.colDef.headerName}</strong>,
        renderCell: (params) => {
          const schedule = params.row.weekSchedule || [];
          const dayName = params.row.weekDayName;

          const slotGroups = new Map();
          schedule.forEach(s => {
            if (!s.timeSlot) return;
            if (!slotGroups.has(s.timeSlot)) slotGroups.set(s.timeSlot, []);
            slotGroups.get(s.timeSlot).push(s.weekDay);
          });

          if (slotGroups.size === 0) {
            return renderTwoLine(params.row.timeSlotNames, dayName);
          }
          if (slotGroups.size === 1) {
            const slot = slotGroups.keys().next().value;
            return renderTwoLine(slot, dayName);
          }

          const sorted = [...slotGroups.entries()].sort((a, b) => b[1].length - a[1].length);
          const [primarySlot, primaryDates] = sorted[0];
          const exceptions = sorted.slice(1);

          const formatDateList = (isoDates) => isoDates
            .map(d => new Date(d))
            .sort((a, b) => a - b)
            .map(d => d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }))
            .join(', ');

          const tooltipBody = (
            <div style={{ fontSize: '0.78rem', lineHeight: 1.5 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{primarySlot}</div>
              <div style={{ opacity: 0.85 }}>{formatDateList(primaryDates)}</div>
              {exceptions.map(([slot, dates], i) => (
                <div key={i} style={{ marginTop: 6 }}>
                  <div style={{ fontWeight: 600 }}>{slot}</div>
                  <div style={{ opacity: 0.85 }}>{formatDateList(dates)}</div>
                </div>
              ))}
            </div>
          );

          return (
            <Tooltip title={tooltipBody} arrow placement="top">
              <div style={{ lineHeight: 1.25, padding: '6px 0', width: '100%', cursor: 'help' }}>
                <div style={{ fontWeight: 500 }}>{primarySlot}</div>
                {dayName && (
                  <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                    {dayName}
                  </div>
                )}
                <div
                  style={{
                    fontSize: '0.65rem',
                    color: 'var(--brand-red)',
                    fontStyle: 'italic',
                    marginTop: 2
                  }}
                >
                  ⚠ {exceptions.length === 1
                    ? `${exceptions[0][0]} · ${exceptions[0][1].length}× ${exceptions[0][1].length === 1 ? (t("week") || "week") : (t("weeks") || "weeks")}`
                    : `${exceptions.length} ${t("variations") || "different slots"}`}
                </div>
              </div>
            </Tooltip>
          );
        },
      },
      {
        field: "roomNames",
        headerName: t("schedulePlanRoom"),
        flex: 1.1,
        minWidth: 120,
        sortable: true,
        headerClassName: 'tableHeaderClassName',
        cellClassName: 'tableCellClassName',
        renderHeader: (params) => <strong>{params.colDef.headerName}</strong>,
        renderCell: (params) => renderTwoLine(params.row.roomNames, params.row.branchObjectNames),
      },
      createNewGeneralColumn("scheduleTypeName", "schedulePlanScheduleType", t, 80),
      {
        field: "facultyName",
        headerName: t("schedulePlanFaculty"),
        flex: 1.2,
        minWidth: 110,
        sortable: true,
        headerClassName: 'tableHeaderClassName',
        cellClassName: 'tableCellClassName',
        renderHeader: (params) => <strong>{params.colDef.headerName}</strong>,
        renderCell: (params) => renderTwoLine(params.row.facultyName, params.row.semesterName),
      },
      {
        field: "weekDayNames",
        headerName: t("schedulePlanWeekDates"),
        flex: 0.7,
        minWidth: 100,
        maxWidth: 140,
        sortable: true,
        headerClassName: 'tableHeaderClassName',
        cellClassName: 'tableCellClassName',
        renderHeader: (params) => <strong>{params.colDef.headerName}</strong>,
        renderCell: (params) => {
          const raw = params.row.weekDayNames || '';
          if (!raw) return <span className="text-muted">-</span>;

          const dates = raw
            .split(',')
            .map(s => s.trim())
            .filter(Boolean)
            .map(s => {
              const m = s.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
              if (!m) return { display: s, ts: Number.MAX_SAFE_INTEGER };
              const [, dd, mm, yyyy] = m;
              return { display: s, ts: new Date(Number(yyyy), Number(mm) - 1, Number(dd)).getTime() };
            })
            .sort((a, b) => a.ts - b.ts);

          const now = new Date(); now.setHours(0, 0, 0, 0);
          const jsDow = now.getDay();
          const offsetToMon = jsDow === 0 ? 6 : jsDow - 1;
          const currentMonStart = new Date(now); currentMonStart.setDate(now.getDate() - offsetToMon);
          const prevMonStart = new Date(currentMonStart); prevMonStart.setDate(currentMonStart.getDate() - 7);
          const nextMonStart = new Date(currentMonStart); nextMonStart.setDate(currentMonStart.getDate() + 7);
          const afterNextMonStart = new Date(currentMonStart); afterNextMonStart.setDate(currentMonStart.getDate() + 14);

          const prevWeek = dates.find(d => d.ts >= prevMonStart.getTime() && d.ts < currentMonStart.getTime());
          const currentWeek = dates.find(d => d.ts >= currentMonStart.getTime() && d.ts < nextMonStart.getTime());
          const nextWeek = dates.find(d => d.ts >= nextMonStart.getTime() && d.ts < afterNextMonStart.getTime());

          const visible = [
            prevWeek && { ...prevWeek, label: 'prev' },
            currentWeek && { ...currentWeek, label: 'current' },
            nextWeek && { ...nextWeek, label: 'next' },
          ].filter(Boolean);

          let displayed = visible;
          if (displayed.length === 0) {
            displayed = [...dates]
              .sort((a, b) => Math.abs(a.ts - now.getTime()) - Math.abs(b.ts - now.getTime()))
              .slice(0, 3)
              .sort((a, b) => a.ts - b.ts);
          }

          const tooltipBody = (
            <div style={{ fontSize: '0.78rem', lineHeight: 1.5 }}>
              {dates.map((d, i) => <div key={i}>{d.display}</div>)}
            </div>
          );

          return (
            <Tooltip title={tooltipBody} arrow placement="left">
              <div style={{ fontSize: '0.72rem', lineHeight: 1.35, padding: '4px 0', cursor: 'help' }}>
                {displayed.map((d, i) => (
                  <div
                    key={i}
                    style={{
                      fontWeight: d.label === 'current' ? 600 : 400,
                      color: d.label === 'current' ? 'var(--brand-red)' : undefined
                    }}
                  >
                    {d.display}
                  </div>
                ))}
                {dates.length > displayed.length && (
                  <div className="text-muted" style={{ fontSize: '0.65rem', fontStyle: 'italic' }}>
                    +{dates.length - displayed.length} more
                  </div>
                )}
              </div>
            </Tooltip>
          );
        },
      },
    ];


    return colDefs;
  }, [t, auth, deleteSchedulePlan, searchQuery]);

  const endpoint = `/StaffPortalStaffs/GetByStaffIdPaged/${auth.universityId}/${auth.userId}`;
  let paramFetch = '';

  const filterParams = useMemo(() => {
    let params = '';
    if (selectedAcademicYearId) {
      params += `&academicYearId=${selectedAcademicYearId.value}`;
    }
    if (selectedFacultyId) {
      params += `&facultyId=${selectedFacultyId.value}`;
    }
    if (selectedLevelId) {
      params += `&levelId=${selectedLevelId.value}`;
    }
    if (selectedSemesterTypeId) {
      params += `&semesterTypeId=${selectedSemesterTypeId.value}`;
    }
    if (selectedSemesterId) {
      params += `&semesterId=${selectedSemesterId.value}`;
    }
    return params;
  }, [selectedAcademicYearId, selectedFacultyId, selectedLevelId, selectedSemesterId, selectedSemesterTypeId]);

  const { data, totalRecords } = useFetchPaginatedDataWithFilters(
    axiosPrivate,
    isInitialLoadComplete ? endpoint : null,
    page + 1,
    pageSize,
    searchQuery,
    filterParams
  );

  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return data;

    const dayOfWeekKey = (row) => {
      const first = (row.weekDayNames || '').split(',')[0]?.trim();
      const m = first?.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
      if (!m) return 99;
      const [, dd, mm, yyyy] = m;
      const jsDow = new Date(Number(yyyy), Number(mm) - 1, Number(dd)).getDay();
      return (jsDow + 6) % 7;
    };

    const timeKey = (row) => {
      const first = (row.timeSlotNames || '').split(',')[0]?.trim();
      const m = first?.match(/^(\d{1,2}):(\d{2})/);
      if (!m) return 9999;
      return Number(m[1]) * 60 + Number(m[2]);
    };

    return [...data].sort((a, b) => {
      const dowDiff = dayOfWeekKey(a) - dayOfWeekKey(b);
      if (dowDiff !== 0) return dowDiff;
      return timeKey(a) - timeKey(b);
    });
  }, [data]);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  return (
    <ListLayout
      title={t("schedulePlanListTitle")}
      searchQuery={searchQuery}
      onSearchChange={handleSearchChange}
      searchPlaceholder={t("searchPlaceholder")}
    >
      <div className="row justify-content-start">
        <FormSelect label={t("academicYear")} name="academicYearId" register={register} errors={errors} options={academicYears} colSize={2} isClearable={true}
          value={selectedAcademicYearId} onChange={(selectedOption) => handleSelectChange('academicYearId', selectedOption)} />
        <FormSelect label={t("faculty")} name="facultyId" register={register} errors={errors} options={faculties} colSize={3} isClearable={true}
          value={selectedFacultyId} onChange={(selectedOption) => handleSelectChange('facultyId', selectedOption)} />
        <FormSelect label="Level" name="levelId" register={register} errors={errors} options={levels} colSize={2} isClearable={true}
          value={selectedLevelId} onChange={(selectedOption) => handleSelectChange('levelId', selectedOption)} />
        <FormSelect label="Semester Type" name="semesterTypeId" register={register} errors={errors} options={semesterTypes} colSize={2} isClearable={true}
          value={selectedSemesterTypeId} onChange={(selectedOption) => handleSelectChange('semesterTypeId', selectedOption)} />
        <FormSelect label="Semester" name="semesterId" register={register} errors={errors} options={semesters} colSize={2} isClearable={true}
          value={selectedSemesterId} onChange={(selectedOption) => handleSelectChange('semesterId', selectedOption)} />
      </div>
      <DataTablePagination
        rows={sortedData}
        columns={columns}
        rowCount={totalRecords}
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        localeTextsMap={localizedTextsMap}
        getRowHeight={() => 'auto'}
        getEstimatedRowHeight={() => 60}
      />
    </ListLayout>
  );
};

export default observer(List);
